/**
 * Pet growth stages.
 *
 * The pet is a single shared creature across all UI variants — a small
 * "λεξοδέντρο" (word tree) that sprouts from a seed and grows leaves as
 * the child plays. Inspired by the Forest app pattern.
 *
 * Growth points sources:
 *   +10 per completed session
 *   +5  per item moved out of the wrongPool (mastered)
 *   +25 bonus for a perfect session
 *
 * Stages 0-4 (thresholds 0/30/80/160/280) are the original pre-v7 progression
 * — kept intact so existing users stay at the same visual stage after the
 * extension. Stages 5-10 are new and follow a ~1.5x accelerating curve so
 * each new milestone takes meaningfully longer than the previous one.
 *
 * Stage 10 is the new max. Reaching it unlocks the "Λεξοδάσος" (word forest)
 * — see `src/data/forestSpecies.js` for the 11 themed trees and
 * `src/utils/forestGrowth.js` for the per-tag growth logic.
 */

export const PET_STAGES = [
  {
    id: 0,
    labelKey: 'seed',
    threshold: 0,
    emoji: '🌰',
  },
  {
    id: 1,
    labelKey: 'sprout',
    threshold: 30,
    emoji: '🌱',
  },
  {
    id: 2,
    labelKey: 'sapling',
    threshold: 80,
    emoji: '🌿',
  },
  {
    id: 3,
    labelKey: 'youngTree',
    threshold: 160,
    emoji: '🪴',
  },
  {
    id: 4,
    labelKey: 'lexoTree',
    threshold: 280,
    emoji: '🌳',
  },
  {
    id: 5,
    labelKey: 'bloomingTree',
    threshold: 420,
    emoji: '🌸',
  },
  {
    id: 6,
    labelKey: 'fruitfulTree',
    threshold: 600,
    emoji: '🍎',
  },
  {
    id: 7,
    labelKey: 'nestedTree',
    threshold: 820,
    emoji: '🪺',
  },
  {
    id: 8,
    labelKey: 'ancientTree',
    threshold: 1100,
    emoji: '🌲',
  },
  {
    id: 9,
    labelKey: 'legendaryTree',
    threshold: 1450,
    emoji: '✨',
  },
  {
    id: 10,
    labelKey: 'worldTree',
    threshold: 1900,
    emoji: '🌟',
  },
];

/**
 * Stage id at which the Λεξοδάσος (forest) unlocks. Same constant powers the
 * unlock check in `PetWidget` (CTA visibility) and `ForestPage` (full-page
 * unlock guard).
 */
export const FOREST_UNLOCK_STAGE = 10;

export const PET_REWARDS = {
  SESSION_COMPLETED: 10,
  ITEM_MASTERED: 5,
  PERFECT_SESSION_BONUS: 25,
};
